// D02 Unit 01 — VLAN Fundamentals (LEARN)
import { predict, observe, readConfirm } from '../../ui/widgets.js';
import { oneSwitch, oneSwitchLayout } from './topologies.js';
import { ran, pinged } from './helpers.js';

export default {
  id: 'u01', num: '01', kind: 'LEARN', title: 'VLAN Fundamentals', type: 'Lesson', implemented: true,
  objective: 'Explain what a VLAN is, why one switch can host several broadcast domains, and prove what a switch with no VLANs does with a broadcast.',
  network: () => oneSwitch({ configured: false }),
  layout: { ...oneSwitchLayout, zones: [{ x: 30, y: 200, w: 840, h: 115, label: 'TODAY: EVERY PORT IN VLAN 1 · one broadcast domain' }] },
  linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'One switch, many broadcast domains', workspace: false,
      content: `
<p>A <b>VLAN</b> (virtual LAN) is a broadcast domain created in software. Ports in VLAN 10 only exchange frames with other ports in VLAN 10, even on the same switch.</p>
<table><tr><th>Without VLANs</th><th>With VLANs</th></tr>
<tr><td>Every broadcast (ARP, DHCP) reaches every port</td><td>Broadcasts stay inside their VLAN</td></tr>
<tr><td>Sales and Support share one L2 domain</td><td>Each department is isolated at Layer 2</td></tr>
<tr><td>Security relies on host firewalls alone</td><td>Traffic between VLANs must pass a router or L3 switch, where it can be filtered</td></tr></table>
<h3>Rules to remember</h3>
<ul><li>Each VLAN is normally one IP subnet: VLAN 10 = 10.10.10.0/24, VLAN 20 = 10.10.20.0/24.</li>
<li>Every port starts in <b>VLAN 1</b> (the default VLAN). Best practice is to move users out of it.</li>
<li>VLANs 1002–1005 are reserved legacy VLANs you will see in <code>show vlan brief</code>.</li>
<li>An <b>access port</b> belongs to exactly one VLAN and sends untagged frames. A <b>trunk</b> carries many VLANs with 802.1Q tags (Unit 03).</li></ul>`,
      widgets: [readConfirm({ id: 'd2u01-read' })],
      question: 'If PC1 and PC3 are in different VLANs on the same switch, what device must be involved for them to talk?',
    },
    {
      id: 'predict', phase: 'PREDICT', title: 'Where does a broadcast go today?', workspace: false,
      content: '<p>SW1 is fresh out of the box. Sales (PC1, PC2) and Support (PC3, PC4) are plugged in. Nobody has created any VLANs yet.</p>',
      widgets: [predict({ id: 'd2u01-pred', prompt: 'PC1 sends an ARP broadcast. Which PCs receive it?', options: ['Only PC2 (same department)', 'PC2, PC3 and PC4 — every other port', 'None — the switch drops broadcasts'] })],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Look at the switch', devices: ['SW1', 'PC1'],
      content: '<p>On <b>SW1</b>: <code>enable</code> then <code>show vlan brief</code>. On <b>PC1</b>: <code>ping 10.10.10.12</code> (PC2), then back on SW1 <code>show mac address-table</code>.</p>',
      evidence: [
        { id: 'vb', text: 'SW1: VLAN table captured', match: ran('SW1', ['show vlan brief', 'show vlan']) },
        { id: 'ping', text: 'PC1: ping to PC2 succeeds', match: pinged('PC1', '10.10.10.12', { success: true }) },
        { id: 'mac', text: 'SW1: MAC table after traffic', match: (ev) => ran('SW1', ['show mac address-table', 'show mac address-table dynamic'])(ev) && /DYNAMIC/.test(ev.output) },
      ],
      widgets: [observe({
        id: 'd2u01-obs', title: 'WHAT THE SWITCH SHOWS',
        fields: [
          { label: 'Which VLAN are Fa0/1 (Sales) and Fa0/11 (Support) in?', check: (v) => v.trim() === '1' || 'Look at which VLAN row lists Fa0/1 and Fa0/11.' },
          { label: 'In which VLAN did SW1 learn PC1\'s MAC?', check: (v, rt) => { const e = rt.net.devices.SW1.macTable.find((x) => x.mac === rt.net.devices.PC1.nic.mac); return e ? (+v === e.vlan || 'Read the Vlan column for PC1\'s MAC.') : 'Generate traffic from PC1 first.'; } },
          { label: 'How many broadcast domains does SW1 have for users right now?', check: (v) => v.trim() === '1' || 'Every user port is in the same VLAN.' },
        ],
      })],
      question: 'The two departments use different subnets, yet share one broadcast domain. Why is that a problem even if they cannot ping each other?',
    },
  ],
  review: (rt) => `<p>You predicted: <b>${rt.widget('d2u01-pred').data.choice || '—'}</b>. The switch put every port in VLAN 1, so PC1's ARP broadcast reached all ports. In the next lab you will split Sales and Support into VLAN 10 and VLAN 20.</p>`,
};
